<script setup lang="ts">
import type { DialogContentEmits, DialogContentProps } from "reka-ui"
import type { HTMLAttributes } from "vue"
import { reactiveOmit } from "@vueuse/core"
import { X } from "lucide-vue-next"
import {
  DialogClose,
  DialogContent,
  DialogPortal,
  useForwardPropsEmits,
} from "reka-ui"
import { cn } from "@/lib/utils"
import DialogOverlay from "./DialogOverlay.vue"

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<DialogContentProps & { class?: HTMLAttributes["class"], showCloseButton?: boolean, mobileFloating?: boolean }>(), {
  showCloseButton: true,
  mobileFloating: true,
})
const emits = defineEmits<DialogContentEmits>()

const delegatedProps = reactiveOmit(props, "class", "showCloseButton", "mobileFloating")

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <DialogPortal>
    <DialogOverlay />
    <DialogContent
      data-slot="dialog-content"
      v-bind="{ ...$attrs, ...forwarded }"
      :class="
        cn(
          props.mobileFloating
            ? 'app-motion-dialog-mobile bg-card fixed bottom-[calc(env(safe-area-inset-bottom)+5.25rem)] left-[50%] z-[110] grid w-[calc(100%-1rem)] max-h-[calc(100svh-6.5rem)] max-w-[calc(100%-1rem)] translate-x-[-50%] translate-y-0 gap-3 rounded-[1.5rem] border p-2 shadow-[0_1px_1px_rgba(15,23,42,0.08),0_10px_24px_rgba(15,23,42,0.12)] sm:top-[50%] sm:bottom-auto sm:w-full sm:max-h-[calc(100vh-2rem)] sm:max-w-[calc(100%-2rem)] sm:translate-y-[-50%] sm:rounded-lg sm:gap-4 sm:p-6 sm:max-w-lg'
            : 'app-motion-dialog bg-card fixed top-[50%] left-[50%] z-[110] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-[0_1px_1px_rgba(15,23,42,0.06),0_10px_24px_rgba(15,23,42,0.08)] sm:max-w-lg',
          props.class,
        )"
    >
      <slot />

      <DialogClose
        v-if="showCloseButton"
        data-slot="dialog-close"
        class="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-3 right-3 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none sm:top-4 sm:right-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
      >
        <X />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
